import { ActivatedRouteSnapshot } from '@angular/router';
import {
  DocumentContextDef,
  DocumentContextType,
  IDocumentTemplate,
} from '@kinet-open-network/shared/data-access/model';
import { isUUID } from 'class-validator';
import { Observable } from 'rxjs';

export interface SendEmailOption {
  label: string;
  value: string;
}

export interface SendEmailOptionsData {
  options: SendEmailOption[];
  default?: string;
}

export interface DocumentsConfig {
  /**
   * Type of the parent entity the documents are attached to (e.g. "deliveries")
   */
  parentType: string;

  /**
   * Context type used to load the available document templates offered when
   * creating a new document (e.g. "delivery")
   */
  templateContextType?: DocumentContextType;

  /**
   * The route parameter to be used for the parent entity id (e.g. "id")
   *
   * @default "id"
   */
  idRouteParam?: string;

  /**
   * Function to extract the template context from the route
   *
   * If not provided, the template context will be extracted from the route parameters
   * using `templateContextType`
   */
  getTemplateContextDef?: (route: ActivatedRouteSnapshot) => DocumentContextDef;

  /**
   * Function to extract the parent entity id from the route
   *
   * If not provided, the parentId will be extracted from the route parameters
   * using `idRouteParam`
   */
  getIdFromRoute?: (route: ActivatedRouteSnapshot) => string;

  /**
   * Provides the selectable email options (and optional default value) offered
   * in the "send document" dialog's email field. If not provided, the email
   * field falls back to an empty, freely editable list.
   */
  getSendEmailOptions?: () => Observable<SendEmailOptionsData>;

  /**
   * Builds the name of a newly created document from its template and the
   * parent entity id.
   *
   * @default `${template.name} ${parentId}`
   */
  getDocumentName?: (template: IDocumentTemplate, parentId: string) => string;
}

export const DEFAULT_DOCUMENTS_CONFIG: Pick<
  DocumentsConfig,
  'idRouteParam' | 'getDocumentName'
> = {
  idRouteParam: 'id',
  getDocumentName: (template, parentId) => {
    const idIsUuid = isUUID(parentId);
    const parts = [template.name];
    if (!idIsUuid) {
      parts.push(parentId);
    }
    return parts.join(' ');
  },
};
